import clsx from 'clsx';
import { MouseEvent, SyntheticEvent, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { noopFunction } from '@vestico/utils';

import { ClientImageSourceSet, CropHint } from '@vestico/model';

import { useElementSize } from '../hooks';
import { useCropHintStyles } from '../hooks/useCropHintStyles';
import { useImageBorderStyles } from '../hooks/useImageStyles';
import { BorderRadiusStyles } from '../model/styles';
import style from './ImageDisplay.module.css';
import { MediaObjectFit } from './MediaDisplayConstants';
import Spinner, { SpinnerPositioning, SpinnerSize } from './Spinner';
import { useImageSources } from './useImageSources';

export interface ImageProps {
  url: string;
  srcSet?: ClientImageSourceSet;
  thumbURL?: string;
  onLoad?: (e: SyntheticEvent<HTMLImageElement>) => void;
  onClick?: (e: MouseEvent<HTMLImageElement>) => void;
  onMouseDown?: (e: MouseEvent<HTMLImageElement>) => void;
  borderRadius?: BorderRadiusStyles;
  animate?: boolean;
  loadInView?: boolean;
  keepPreviousUntilLoaded?: boolean;
  imageFit?: MediaObjectFit;
  cropHint?: CropHint;
}

export const ImageDisplay = ({
  url,
  srcSet,
  thumbURL,
  onLoad,
  onClick,
  onMouseDown,
  borderRadius,
  loadInView,
  animate,
  keepPreviousUntilLoaded,
  imageFit,
  cropHint,
}: ImageProps) => {
  const [previousLoadedThumbImageURL, setPreviouLoadedThumbImageURL] = useState<undefined | string>(undefined);
  const [previousLoadedMainImageURL, setPreviousLoadedMainImageURL] = useState<undefined | string>(undefined);
  const [loadedThumbImageURL, setLoadedThumbImageURL] = useState<undefined | string>(undefined);
  const [loadedMainImageURL, setLoadedMainImageURL] = useState<undefined | string>(undefined);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const { ref: inViewRef, inView } = useInView();
  const imgRef = useRef<HTMLImageElement>(null);

  const [mediaAspectRatio, setMediaAspectRatio] = useState<number | undefined>(undefined);

  const allowLoadingMain = useMemo(() => !loadInView || inView, [inView, loadInView]);

  // Use `useCallback` so we don't recreate the function on each render
  const setRefs = useCallback(
    (node: HTMLDivElement) => {
      // Ref's from useRef needs to have the node assigned to `current`
      containerRef.current = node;
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node);
    },
    [inViewRef]
  );

  const containerDimensions = useElementSize(containerRef);

  const onThumbLoad = useCallback(
    (e: Event) => {
      const { width, height } = e.currentTarget as HTMLImageElement;
      if (width && height) {
        setMediaAspectRatio(width / height);
      }
      setLoadedThumbImageURL(thumbURL);
    },
    [thumbURL]
  );

  useLayoutEffect(() => {
    const hasAlreadyLoadedMain = loadedMainImageURL === url;
    const hasAlreadyLoadedThumb = loadedThumbImageURL === thumbURL;

    let thumbImgElement: HTMLImageElement | undefined;

    if (thumbURL && !hasAlreadyLoadedThumb) {
      setPreviouLoadedThumbImageURL(thumbURL);
      setLoadedThumbImageURL(undefined);
      setMediaAspectRatio(undefined);
      const img = new Image();
      img.src = thumbURL;
      img.decoding = 'async';
      img.onload = onThumbLoad;
      thumbImgElement = img;
    }

    if (!hasAlreadyLoadedMain && allowLoadingMain) {
      setPreviousLoadedMainImageURL(url);
      setLoadedMainImageURL(undefined);
    }

    // Reset `onload` when unmounting the component
    return () => {
      if (thumbImgElement) {
        thumbImgElement.onload = null;
      }
    };
  }, [allowLoadingMain, loadedMainImageURL, loadedThumbImageURL, onThumbLoad, thumbURL, url]);

  const borderStyles = useImageBorderStyles(borderRadius);

  const isLoadingThumb = useMemo(() => thumbURL && loadedMainImageURL !== url, [thumbURL, loadedMainImageURL, url]);

  const imgSrc = useMemo(() => {
    if (loadedMainImageURL) {
      return loadedMainImageURL;
    }
    if (loadedThumbImageURL) {
      return loadedThumbImageURL;
    }
    if (previousLoadedMainImageURL && keepPreviousUntilLoaded) {
      return previousLoadedMainImageURL;
    }
    if (previousLoadedThumbImageURL && keepPreviousUntilLoaded) {
      return previousLoadedThumbImageURL;
    }
    return null;
  }, [keepPreviousUntilLoaded, loadedMainImageURL, loadedThumbImageURL, previousLoadedMainImageURL, previousLoadedThumbImageURL]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLImageElement>) => {
      if (onClick) {
        e.preventDefault();
        onClick(e);
      }
    },
    [onClick]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLImageElement>) => {
      if (onMouseDown) {
        e.preventDefault();
        onMouseDown(e);
      }
    },
    [onMouseDown]
  );

  useLayoutEffect(() => {
    const { current: img } = imgRef;
    if (!img) {
      return noopFunction;
    }

    const id = requestAnimationFrame(() => {
      img.classList.toggle(clsx(style.loaded), !isLoadingThumb);
    });
    return () => cancelAnimationFrame(id);
  }, [isLoadingThumb, loadedMainImageURL]);

  const { css: imageCSS } = useCropHintStyles({ cropHint, otherStyles: borderStyles, mediaAspectRatio, containerDimensions });

  const onLoadMainImage = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const loadedURL = event.currentTarget.src;
      const loadedMainImage = loadedURL === url;

      if (!mediaAspectRatio) {
        const { width, height } = event.currentTarget;
        if (width && height) {
          setMediaAspectRatio(width / height);
        }
      }
      if (loadedMainImage) {
        setLoadedMainImageURL(url);
        if (onLoad) {
          onLoad(event);
        }
      }
    },
    [mediaAspectRatio, onLoad, url]
  );

  const didLoadThumb = useMemo(() => loadedThumbImageURL === thumbURL, [loadedThumbImageURL, thumbURL]);

  const { sources, onImageError: onShadowImageError } = useImageSources({ url, srcSet, mediaAspectRatio, didLoadThumb, containerDimensions });

  const shadowMainImageMarkup =
    didLoadThumb && allowLoadingMain && !loadedMainImageURL ? (
      <picture>
        {sources}
        {/* skipcq JS-W1015 */}
        <img
          src={url}
          className={clsx(style.image, style.shadow, { [style.contain]: imageFit === 'contain' })}
          draggable={false}
          onLoad={onLoadMainImage}
          onError={onShadowImageError}
          style={imageCSS}
          alt=""
          aria-hidden="true"
        />
      </picture>
    ) : null;

  const imageMarkup = imgSrc ? (
    // skipcq JS-W1015
    <picture>
      {!isLoadingThumb ? sources : undefined}
      <img
        src={imgSrc}
        className={clsx(style.image, { [style.animate]: animate, [style.contain]: imageFit === 'contain' })}
        draggable={false}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        style={imageCSS}
        alt=""
        aria-hidden="true"
        ref={imgRef}
      />
    </picture>
  ) : null;

  const spinnerMarkup =
    !loadedMainImageURL && !loadedThumbImageURL ? <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.flex} /> : null;

  return (
    <div className={clsx(style.root)} ref={setRefs} style={borderStyles}>
      {spinnerMarkup}
      {imageMarkup}
      {shadowMainImageMarkup}
    </div>
  );
};
